import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FiBookOpen, FiSearch, FiClock } from "react-icons/fi";
import { API_BASE } from "../../../api";
import { ENDPOINTS as LMS_ENDPOINTS } from "../Utils/Endpoints";
import { Models } from "../Utils/ModelsLMS";
import { getSessionUserId } from "../../../utils/SessionUser";
import AlertDialog from "../Components/Shared/AlertDialog";
import ConfirmDialog from "../Components/Shared/ConfirmDialog";

const CarrerasMarketplace = () => {
    const navigate = useNavigate();
    const [carreras, setCarreras] = useState([]);
    const [loading, setLoading] = useState(false);
    const [inscripciones, setInscripciones] = useState([]); // CarreraEstudiante existentes
    const [idEstudiante, setIdEstudiante] = useState(null);
    const [registeringId, setRegisteringId] = useState(null);
    const [validationsComplete, setValidationsComplete] = useState(false);

    // Filtros visuales
    const [searchTerm, setSearchTerm] = useState("");

    // Estados para modales
    const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', variant: 'info' });
    const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

    const SERVER_URL = API_BASE.replace(/\/api\/?$/, "");

    // 1. Cargar idEstudiante
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const idUsuario = getSessionUserId();
                if (!idUsuario) {
                    setValidationsComplete(true);
                    return;
                }

                const resEstudiante = await axios.get(LMS_ENDPOINTS.estudianteByUsuario(idUsuario)).catch(() => ({ data: null }));
                const estudianteRecord = resEstudiante.data ? Models.estudiante(resEstudiante.data) : null;

                if (estudianteRecord) {
                    setIdEstudiante(estudianteRecord.idEstudiante ?? estudianteRecord.IdEstudiante);
                }

                setValidationsComplete(true);
            } catch (error) {
                console.error('[CarrerasMarketplace] Error en validación inicial:', error);
                setValidationsComplete(true);
            }
        };

        loadInitialData();
    }, []);

    // 2. Debounce para la búsqueda (600ms)
    const [debouncedSearch, setDebouncedSearch] = useState("");
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedSearch(searchTerm), 600);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    // 3. Cargar Carreras e Inscripciones
    useEffect(() => {
        if (!validationsComplete || !idEstudiante) return;

        const loadContent = async () => {
            setLoading(true);
            try {
                const [resCarreras, resInscripciones] = await Promise.all([
                    axios.get(LMS_ENDPOINTS.carreras),
                    axios.get(LMS_ENDPOINTS.carreraEstudiante)
                ]);

                // Filter inscripciones locally (assuming endpoint returns all)
                // If the endpoint is huge, backend filtering would be better, but sticking to pattern
                const allInscripciones = resInscripciones.data || [];
                const misInscripciones = allInscripciones.filter(i => Number(i.idEstudiante || i.IdEstudiante) === Number(idEstudiante));

                setInscripciones(misInscripciones);
                setCarreras(resCarreras.data || []);
            } catch (error) {
                console.error("[CarrerasMarketplace] Error cargando contenido:", error);
            } finally {
                setLoading(false);
            }
        };

        loadContent();
    }, [validationsComplete, idEstudiante]);

    // handlers
    const handleRegister = async (carrera) => {
        if (!idEstudiante) {
            setAlertDialog({
                open: true,
                title: 'Error',
                message: 'No se pudo identificar al estudiante actual. No es posible registrar la carrera.',
                variant: 'error'
            });
            return;
        }

        const idCarrera = carrera.idCarrera || carrera.id || carrera.IdCarrera;
        if (!idCarrera) {
            setAlertDialog({ open: true, title: 'Error', message: 'ID de carrera no válido.', variant: 'error' });
            return;
        }

        const yaInscrito = inscripciones.some(r => Number(r.idCarrera || r.IdCarrera) === Number(idCarrera));
        if (yaInscrito) {
            setAlertDialog({ open: true, title: 'Información', message: 'Ya estás inscrito en esta carrera.', variant: 'info' });
            setCarreras(prev => prev.filter(c => Number(c.idCarrera || c.id) !== Number(idCarrera)));
            return;
        }

        setConfirmDialog({
            open: true,
            title: 'Confirmar inscripción',
            message: `¿Deseas registrarte en la carrera "${carrera.nombre}"?`,
            onConfirm: () => proceedWithRegistration(carrera, idCarrera)
        });
    };

    const proceedWithRegistration = async (carrera, idCarrera) => {
        try {
            setRegisteringId(idCarrera);

            // DTO para CarreraEstudiante
            const dto = {
                IdCarreraEstudiante: 0,
                IdEstudiante: Number(idEstudiante),
                IdCarrera: Number(idCarrera),
                Activo: true,
                FechaInscripcion: new Date().toISOString()
            };

            await axios.post(LMS_ENDPOINTS.carreraEstudiante, dto);

            // Actualizar estado local
            setInscripciones(prev => [...prev, { ...dto, idCarrera: idCarrera, idEstudiante: idEstudiante }]); // Add simple obj to local filter
            setCarreras(prev => prev.filter(c => Number(c.idCarrera || c.id) !== Number(idCarrera))); // Remove immediately from view

            setAlertDialog({ open: true, title: '¡Éxito!', message: 'Te has registrado correctamente en la carrera.', variant: 'success' });
        } catch (error) {
            console.error("Error registration:", error);
            setAlertDialog({ open: true, title: 'Error', message: 'Ocurrió un error al registrar la carrera.', variant: 'error' });
        } finally {
            setRegisteringId(null);
        }
    };

    return (
        <div className="panel-sigma-main">
            <div className="profile-header-title">
                <h1>Oferta de Carreras</h1>
            </div>

            {/* UI de Búsqueda */}
            <div style={{ marginBottom: '2rem', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.05))' }}>
                <div style={{
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '0.8rem 1rem',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '1rem',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    {/* Búsqueda */}
                    <div style={{
                        flex: 1,
                        minWidth: '200px',
                        display: 'flex',
                        alignItems: 'center',
                        background: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '999px',
                        padding: '0.4rem 1rem',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}>
                        <FiSearch style={{ color: 'var(--color-primary-dark)', marginRight: '0.6rem', fontSize: '1rem' }} />
                        <input
                            type="text"
                            className="lms-search-input"
                            placeholder="Buscar carreras..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ border: 'none', outline: 'none', background: 'transparent', width: '100%', fontSize: '0.9rem', color: '#1e293b' }}
                        />
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="catalog-loading">Cargando carreras...</div>
            ) : (
                <div className="courses-grid fixed-cards">
                    {(() => {
                        // Filtrar carreras
                        const visibles = (!carreras ? [] : carreras).filter((carrera) => {
                            // 1. Debe estar publicada
                            const isPublished = carrera.publicado || carrera.Publicado;
                            if (!isPublished) return false;

                            // 2. Debe tener certificado asignado (Maestro)
                            const idCertificado = carrera.idCertificado || carrera.IdCertificado;
                            if (!idCertificado) return false;

                            // 3. No debe estar ya inscrita
                            const idCarrera = carrera.idCarrera || carrera.id || carrera.IdCarrera;
                            if (!idCarrera) return false;

                            const inscrito = inscripciones.some(
                                (rel) => Number(rel.idCarrera || rel.IdCarrera) === Number(idCarrera)
                            );
                            if (inscrito) return false;

                            // 3. Filtro de búsqueda
                            if (debouncedSearch) {
                                const name = (carrera.nombre || carrera.Nombre || '').toLowerCase();
                                const desc = (carrera.descripcion || carrera.Descripcion || '').toLowerCase();
                                const search = debouncedSearch.toLowerCase();
                                if (!name.includes(search) && !desc.includes(search)) return false;
                            }

                            return true;
                        });

                        if (visibles.length === 0) {
                            return (
                                <div
                                    style={{
                                        gridColumn: "1 / -1",
                                        textAlign: "center",
                                        padding: "3rem",
                                        color: "#94a3b8",
                                    }}
                                >
                                    No hay carreras disponibles para inscripción en este momento.
                                </div>
                            );
                        }

                        return visibles.map((carrera, index) => {
                            const imagePath =
                                carrera.urlImagen ||
                                carrera.UrlImagen ||
                                "";
                            const hasImage = !!imagePath;

                            const imageUrl = imagePath.startsWith('http://') || imagePath.startsWith('https://')
                                ? imagePath
                                : `${SERVER_URL}${imagePath}`;

                            return (
                                <article key={carrera.idCarrera || index} className="course-card fixed-card">
                                    {hasImage ? (
                                        <div className="course-thumb" style={{ padding: 0 }}>
                                            <img
                                                src={imageUrl}
                                                alt={carrera.nombre || carrera.Nombre || "Portada de carrera"}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "fill",
                                                    display: "block",
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <div
                                            className={`course-thumb course-thumb-1`}
                                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e2e8f0', color: '#94a3b8' }}
                                        >
                                            <FiBookOpen size={48} />
                                        </div>
                                    )}
                                    <div className="course-body">
                                        <h3 className="course-title">{carrera.nombre || carrera.Nombre}</h3>
                                        <div style={{ marginTop: "0.8rem", flex: 1, display: 'flex', flexDirection: 'column' }}>
                                            <p style={{
                                                fontSize: '0.85rem',
                                                color: '#64748b',
                                                lineHeight: 1.5,
                                                marginBottom: '1rem',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden'
                                            }}>
                                                {carrera.descripcion || carrera.Descripcion || "Sin descripción disponible."}
                                            </p>

                                            <div style={{ marginTop: 'auto' }}>
                                                {/* Espacio para info extra si se requiere */}
                                            </div>
                                        </div>
                                        <div className="course-actions">
                                            <button
                                                type="button"
                                                className="btn-action"
                                                style={{
                                                    width: '100%',
                                                    padding: '0.5rem 1rem',
                                                    cursor: 'pointer',
                                                    transition: '0.2s',
                                                    fontFamily: "'Outfit', sans-serif"
                                                }}
                                                onClick={() => handleRegister(carrera)}
                                                disabled={registeringId === (carrera.idCarrera || carrera.id)}
                                            >
                                                {registeringId === (carrera.idCarrera || carrera.id)
                                                    ? "Registrando..."
                                                    : "Registrarte"}
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            );
                        });
                    })()}
                </div>
            )}

            {/* Modales */}
            <AlertDialog
                open={alertDialog.open}
                onClose={() => setAlertDialog({ ...alertDialog, open: false })}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
            />

            <ConfirmDialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
                onConfirm={confirmDialog.onConfirm}
                title={confirmDialog.title}
                message={confirmDialog.message}
            />
        </div>
    );
};

export default CarrerasMarketplace;
